import { useMemo } from 'react';
import { useAppStore } from '../../../store/useAppStore';

import { usePresence } from '../presence';
import type { ProjectScope } from '../project-scope';
import { deriveNeededProjectDataResources } from '../project-data-requirements';
import { useProjectSyncCoordinator } from './useProjectSyncCoordinator';

export { extractCloudState } from '../project-cloud-state-extraction';
export {
    clearAcknowledgedPendingGlobalCloudField,
    cloudComparableShowStructure,
    createLoadedResourcesForCloudBaseline,
    getCloudAutosaveLoadedResources,
    getPendingGlobalCloudFieldsForAutosave,
    hasLoadedInitialCloudResources,
    mergeCueAnchorsWithPendingLocal,
    mergeCustomCuesWithPendingLocal,
    mergeFlyCuesWithPendingLocal,
    mergeFollowSpotCuesWithPendingLocal,
    mergeFollowSpotsWithPendingLocal,
    mergeGlobalCloudFieldWithPendingLocal,
    mergeLightingCuesWithPendingLocal,
    mergeMicAssignmentsWithPendingLocal,
    mergeMicPackConfigWithPendingLocal,
    mergeMicSwapsWithPendingLocal,
    mergeProjectionCuesWithPendingLocal,
    mergePropsWithPendingLocal,
    mergeSetsWithPendingLocal,
    mergeShowStructureForCloudSnapshot,
    mergeSoundClipsWithPendingLocal,
    mergeSoundCuesWithPendingLocal,
    projectMetaComparableForCloudSnapshot,
    shouldSaveScriptBlocksForAutosave,
    shouldSkipPreloadedResourceSnapshot,
} from '../project-remote-update';
export {
    shouldApplyRemoteSnapshotForCloudAuthority,
    shouldSkipCloudAutosaveForMetaUpdate,
    shouldSkipCloudAutosaveForRemoteUpdate,
    shouldSkipQueuedCloudAutosaveForRemoteVersion,
} from '../project-autosave-coordinator';

export interface UseSyncOptions {
    projectDataResources?: readonly string[];
}

export const useSync = (options: UseSyncOptions = {}) => {
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const cloudOrgId = useAppStore(state => state.cloud.orgId);
    const cloudProjectId = useAppStore(state => state.cloud.projectId);
    const hasLoadedCloudProject = useAppStore(state => state.cloud.hasLoadedCloudProject === true);
    const authoritativeResourceIds = useAppStore(state => state.cloud.authoritativeResourceIds);
    const setCloudState = useAppStore(state => state.metaActions.setCloudState);
    const activeTab = useAppStore(state => state.ui.activeTab);
    const user = useAppStore(state => state.auth.user);
    const isAuthInitialized = useAppStore(state => state.auth.initialized);

    usePresence();

    const shouldSync = !!user && isAuthInitialized;
    const projectScope = useMemo<ProjectScope | null>(() => {
        if (!cloudOrgId || !cloudProjectId) return null;
        return { orgId: cloudOrgId, projectId: cloudProjectId };
    }, [cloudOrgId, cloudProjectId]);

    const activeToolResources = useMemo(() => deriveNeededProjectDataResources(activeTab), [activeTab]);
    const requestedResourceKey = options.projectDataResources?.join('\u001f') ?? null;
    const neededResources = useMemo(
        () => requestedResourceKey === null
            ? activeToolResources
            : requestedResourceKey === '' ? [] : requestedResourceKey.split('\u001f'),
        [activeToolResources, requestedResourceKey],
    );

    return useProjectSyncCoordinator({
        cloudEnabled,
        projectScope,
        shouldSync,
        user,
        neededResources,
        hasLoadedCloudProject,
        authoritativeResourceIds,
        setCloudState,
    });
};
